'use client'
import { useEffect, useRef, useState } from 'react'
import { LightbulbIcon } from '@repo/ui/LightbulbIcon'
import { DocumentIcon } from '@repo/ui/DocumentIcon'
import { CrossIcon } from '@repo/ui/CrossIcon'
import { useDocumentBundleWizardStore } from '@repo/store/useDocumentBundleWizardStore'
import { useDocumentBundleAuthStore } from '@repo/store/useDocumentBundleAuthStore'
import { useWizardStore } from '@repo/store/useWizardStore'
import { LoadingState } from '@repo/ui/LoadingState'
import { MatterSelect } from '@repo/ui/MatterSelect'
import { WizardFooter } from '@repo/ui/WizardFooter'
import { TextLinkButton } from '@repo/ui/TextLinkButton'
import { WizardPanel } from '@repo/ui/WizardPanel'
import MUIStack from '@mui/material/Stack'
import MUITextField from '@mui/material/TextField'
import MUITypography from '@mui/material/Typography'
import MUIAlert from '@mui/material/Alert'
import MUIAlertTitle from '@mui/material/AlertTitle'
import MUIPaper from '@mui/material/Paper'
import MUIBox from '@mui/material/Box'
import { DocumentBundleSection } from './DocumentBundleSection'
import { DocumentBundleSectionDetails } from './DocumentBundleSectionDetails'
import { DocumentBundlesSummary } from './DocumentBundlesSummary'
import { confirm as reactConfirm } from '@repo/ui/Confirm'
import { useTheme } from '@mui/material/styles'

type StepProps = {
  wideMode?: boolean
  handlePrevNext: any
  updateStepState: any
  wizardId?: string
}

const BUNDLEDOCS_AUTH_REQUIRED_MESSAGE = 'You must authenticate with Bundledocs before performing this action.'
const BUNDLEDOCS_AUTH_DEPENDENCY_FAILED_STATUS = 424

const isBundledocsAuthRequiredError = (err: any) => {
  const status = err?.status || err?.response?.status
  if (status === BUNDLEDOCS_AUTH_DEPENDENCY_FAILED_STATUS) return true

  const message =
    err?.response?.data?.detail || err?.response?.data?.title || err?.response?.data?.message || err?.message
  return typeof message === 'string' && message.includes(BUNDLEDOCS_AUTH_REQUIRED_MESSAGE)
}

const MAX_CREATE_BUNDLE_ATTEMPTS = 5
const CREDENTIAL_SETTLE_DELAY_MS = 1500
const AUTH_POLL_INTERVAL_MS = 2000
const MAX_AUTH_POLL_ATTEMPTS = 15 // ~30s, enough time for a user to enter Bundledocs credentials

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export const CreateDocumentBundles = ({ wideMode, handlePrevNext, updateStepState, wizardId }: StepProps) => {
  const theme = useTheme()
  const {
    mode,
    selectedMatter,
    bundleName,
    description,
    updateState,
    addSection,
    clearSections,
    clearIndexedSection,
    moveSectionUp,
    moveSectionDown,
    updateSectionName,
    createBundle,
    updateBundle,
    createSections,
    fetchBundleSections
  } = useDocumentBundleWizardStore()
  const {
    IsAuthenticatedUser,
    getLoginUrl,
    isAuthenticating,
    error: authError,
    updateState: updateAuthState
  } = useDocumentBundleAuthStore()
  const sections = useDocumentBundleWizardStore((state) => state.sections)
  const { setWizardConfig } = useWizardStore()
  const isEditMode = mode === 'edit'
  const sectionStepsLockedRef = useRef(false)
  // Set when Create Bundle just finished flipping to Edit Mode - navigation to the first section
  // step must wait until WizardBase has re-rendered with the rebuilt step list (see the effect
  // below), since calling handlePrevNext synchronously right after the mode flip would still use
  // the pre-flip step list. This is state (not a ref) specifically so that setting it always forces
  // at least one more render/effect check - by then handlePrevNext (passed down from WizardBase)
  // reflects whatever the wizard config's current state is, however many renders that took.
  const [shouldNavigateToFirstSection, setShouldNavigateToFirstSection] = useState(false)
  const [sectionNameErrors, setSectionNameErrors] = useState<Record<string, string>>({})
  const [workflowStage, setWorkflowStage] = useState<
    'authenticating' | 'creating-bundle' | 'updating-bundle' | 'creating-sections' | 'completed' | null
  >(null)

  const workflowStageLabel = {
    authenticating: 'Authenticating...',
    'creating-bundle': 'Creating Bundle...',
    'updating-bundle': 'Updating Bundle...',
    'creating-sections': 'Creating Sections...',
    completed: 'Completed'
  }[workflowStage as string]

  useEffect(() => {
    if (!wizardId) return
    if (!isEditMode && sectionStepsLockedRef.current) return

    setWizardConfig({
      wizardId,
      wizardConfig: [
        {
          stageLabel: isEditMode ? 'Edit Bundle' : 'Create Bundle',
          stageId: 'create-bundle',
          steps: [
            {
              stepLabel: 'Document bundle details',
              stepDescription: isEditMode
                ? 'Please update the document bundle details.'
                : 'Please enter the document bundle details.',
              stepId: 'create-document-bundle',
              skippable: false,
              component: CreateDocumentBundles,
              uniqueId: 'create-document-bundle'
            },
            ...sections.map((section) => ({
              stepLabel: section.name,
              stepDescription: `Configure ${section.name}.`,
              stepId: `document-bundle-section-${encodeURIComponent(section.name)}`,
              skippable: false,
              component: DocumentBundleSectionDetails,
              uniqueId: `document-bundle-section-${encodeURIComponent(section.name)}`,
              sectionId: section.id,
              contentSx: { backgroundColor: 'action.hover' }
            })),
            {
              stepLabel: 'Summary',
              stepDescription: 'summary',
              stepId: 'bundle-summary',
              skippable: false,
              component: DocumentBundlesSummary,
              uniqueId: 'document-bundle-create-bundle'
            }
          ]
        }
      ]
    })
  }, [sections, wizardId, setWizardConfig, isEditMode])
  useEffect(() => {
    if (!shouldNavigateToFirstSection) return
    setShouldNavigateToFirstSection(false)
    handlePrevNext(1)
  }, [shouldNavigateToFirstSection, handlePrevNext])

  const authenticateWithBundledocs = async () => {
    setWorkflowStage('authenticating')

    const loginUrl = await getLoginUrl({ clientNo: selectedMatter?.clientNo, matterNo: selectedMatter?.matterNo })

    if (!loginUrl) throw new Error('Unable to start Bundledocs authentication')

    window.open(loginUrl, '_blank', 'noopener,noreferrer')

    for (let attempt = 1; attempt <= MAX_AUTH_POLL_ATTEMPTS; attempt++) {
      await delay(AUTH_POLL_INTERVAL_MS)

      if (await IsAuthenticatedUser()) {
        setWorkflowStage(isEditMode ? 'updating-bundle' : 'creating-bundle')
        return
      }
    }

    throw new Error(BUNDLEDOCS_AUTH_REQUIRED_MESSAGE)
  }

  const ensureBundledocsActionCompleted = async (performAction) => {
    let hasAuthenticatedThisAttempt = false

    for (let attempt = 1; attempt <= MAX_CREATE_BUNDLE_ATTEMPTS; attempt++) {
      const isLastAttempt = attempt === MAX_CREATE_BUNDLE_ATTEMPTS

      if (hasAuthenticatedThisAttempt) {
        const authenticated = await IsAuthenticatedUser()

        if (!authenticated) {
          if (isLastAttempt) throw new Error(BUNDLEDOCS_AUTH_REQUIRED_MESSAGE)
          await delay(CREDENTIAL_SETTLE_DELAY_MS)
          continue
        }
      }

      try {
        await performAction(!isLastAttempt)
        return
      } catch (err: any) {
        if (!isBundledocsAuthRequiredError(err) || isLastAttempt) throw err

        if (!hasAuthenticatedThisAttempt) {
          await authenticateWithBundledocs()
          hasAuthenticatedThisAttempt = true
        }
      }
    }
  }
  const handleContinue = async () => {
    if (!selectedMatter || !bundleName.trim() || !description.trim()) {
      return
    }

    updateAuthState({ key: 'isAuthenticating', value: true })
    updateAuthState({ key: 'error', value: null })
    setWorkflowStage(isEditMode ? 'updating-bundle' : 'creating-bundle')

    try {
      const authenticated = await IsAuthenticatedUser()

      if (!authenticated) {
        await authenticateWithBundledocs()
      }

      if (isEditMode) {
        await ensureBundledocsActionCompleted((suppressErrorToast) => updateBundle({ suppressErrorToast }))
      } else {
        await ensureBundledocsActionCompleted((suppressErrorToast) => createBundle({ suppressErrorToast }))

        setWorkflowStage('creating-sections')
        sectionStepsLockedRef.current = true
        await createSections()
        const { matterBundleId } = useDocumentBundleWizardStore.getState()
        await fetchBundleSections({ matterBundleId })

        // The bundle now exists - treat every subsequent Continue as editing an existing bundle
        // (reusing the already-implemented Update Bundle flow) instead of creating it again.
        updateState({ key: 'mode', value: 'edit' })
        sectionStepsLockedRef.current = false
      }

      setWorkflowStage('completed')

      updateStepState({
        status: 'completed',
        statusText: 'Complete'
      })

      if (isEditMode) {
        handlePrevNext(1)
      } else {
        setShouldNavigateToFirstSection(true)
      }
    } catch (err: any) {
      console.error('Error saving document bundle:', err)
      updateAuthState({
        key: 'error',
        value: err?.message || `Failed to ${isEditMode ? 'update' : 'create'} the document bundle`
      })
    } finally {
      updateAuthState({ key: 'isAuthenticating', value: false })
      setWorkflowStage(null)
    }
  }
  const handleDeleteAllSections = async () => {
    if (sections.length == 0) return
    const proceed = await reactConfirm('Are you sure you want to delete all sections?')
    if (!proceed) return
    clearSections()
  }
  const handleAddSection = () => {
    addSection()
    const sections = useDocumentBundleWizardStore.getState().sections
    console.log(sections)
  }
  const handleSectionNameChange = (id, name) => {
    const success = updateSectionName(id, name)

    setSectionNameErrors((prev) => ({
      ...prev,
      [id]: success ? '' : 'Section name must be unique'
    }))
  }
  const handleMoveSectionUp = (index) => {
    moveSectionUp(index)
    const sections = useDocumentBundleWizardStore.getState().sections
    console.log(sections)
  }
  const handleMoveSectionDown = (index) => {
    moveSectionDown(index)
    const sections = useDocumentBundleWizardStore.getState().sections
    console.log(sections)
  }
  const handleDeleteSection = async (sectionName, index) => {
    const proceed = await reactConfirm(`Are you sure you want to delete ${sectionName}?`)
    if (!proceed) return
    clearIndexedSection(index)
    const sections = useDocumentBundleWizardStore.getState().sections
    console.log(sections)
  }
  return (
    <>
      <MUIAlert data-testid="WizardPrompt" severity="info" sx={{ mb: 6, width: '100%' }} icon={<LightbulbIcon />}>
        <MUITypography variant="h5" sx={{ mb: 0, color: 'succes.dark' }}>
          Tip
        </MUITypography>
        <MUITypography variant="body1" sx={{ mb: 0, color: 'succes.dark' }}>
          This screen will let you <strong>{isEditMode ? 'edit this document bundle' : 'create a new document bundle'}</strong>.
        </MUITypography>
      </MUIAlert>

      {authError && (
        <MUIAlert
          severity={authError.includes(BUNDLEDOCS_AUTH_REQUIRED_MESSAGE) ? 'warning' : 'error'}
          sx={{ mb: 6, width: '100%' }}
        >
          <MUIAlertTitle>{authError.includes(BUNDLEDOCS_AUTH_REQUIRED_MESSAGE) ? 'Warning' : 'Error'}</MUIAlertTitle>
          {authError}
        </MUIAlert>
      )}

      <MUIStack direction="column" gap={8}>
        <WizardPanel data-testid="DocumentBundleDetailsStep">
          <MUIStack spacing={4}>
            {workflowStage && (
              <MUIStack direction="row" alignItems="center" spacing={2}>
                <LoadingState sx={{ width: '32px', height: '32px' }} />
                <MUITypography sx={{ mb: 0 }}>{workflowStageLabel}</MUITypography>
              </MUIStack>
            )}

            <MUIStack spacing={6}>
              <MUIBox
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 3
                }}
              >
                <DocumentIcon sx={{ width: 32, height: 32, mb: 3 }} />

                <MUITypography variant="h5" sx={{ mb: 3 }}>
                  Document bundle details
                </MUITypography>
              </MUIBox>

              <MUIStack direction="row" spacing={6} alignItems="flex-start">
                <MatterSelect
                  size="medium"
                  label="Client and matter"
                  sx={{ flex: 1 }}
                  value={selectedMatter}
                  disabled={isEditMode}
                  onChange={(value: any) => {
                    updateState({
                      key: 'selectedMatter',
                      value
                    })

                    updateState({
                      key: 'bundleName',
                      value: `${value?.clientMatterNo ?? ''}`
                    })
                  }}
                  required
                />

                <MUITextField
                  label="Matter no"
                  value={selectedMatter?.matterNo ?? ''}
                  InputProps={{
                    readOnly: true
                  }}
                  sx={{ width: 180, mt: '16px' }}
                />
              </MUIStack>

              <MUITextField
                label="Bundle description"
                fullWidth
                value={description}
                onChange={(e) =>
                  updateState({
                    key: 'description',
                    value: e.target.value.slice(0, 512)
                  })
                }
                inputProps={{
                  maxLength: 512
                }}
                required
              />
            </MUIStack>
          </MUIStack>
        </WizardPanel>

        <MUIStack direction="row" justifyContent="space-between" alignItems="center">
          <MUITypography variant="h6" sx={{ mb: 0 }}>
            Sections
          </MUITypography>

          <TextLinkButton
            title="Delete all sections"
            isActive={sections.length > 1}
            sx={{ color: 'primary.main', width: 'auto', padding: 0 }}
            onClick={handleDeleteAllSections}
          />
        </MUIStack>

        {sections.length > 0 && (
          <MUIPaper
            elevation={0}
            sx={{
              p: 6,
              backgroundColor: 'common.white',
              border: 'none',
              boxShadow: 'none',
              borderRadius: 1
            }}
          >
            <MUIStack spacing={6} mt={3}>
              {sections.map((section, index) => (
                <DocumentBundleSection
                  key={section.id}
                  section={section}
                  isFirst={index === 0}
                  isLast={index === sections.length - 1}
                  isEditMode={isEditMode}
                  error={sectionNameErrors[section.id]}
                  onNameChange={handleSectionNameChange}
                  onMoveUp={() => handleMoveSectionUp(index)}
                  onMoveDown={() => handleMoveSectionDown(index)}
                  onDelete={() => handleDeleteSection(section.name, index)}
                />
              ))}
            </MUIStack>
          </MUIPaper>
        )}

        <TextLinkButton
          onClick={handleAddSection}
          sx={{ mt: 1, ml: 5, width: '300px', mb: 8 }}
          title={
            <MUIStack direction="row" alignItems="center" gap={2}>
              <CrossIcon
                sx={{
                  transform: 'rotate(45deg)',
                  width: '24px',
                  height: '24px',
                  color: theme?.palette?.secondary?.dark || 'black'
                }}
              />
              Add Section
            </MUIStack>
          }
        />

        <WizardFooter
          wideMode={wideMode}
          backButtonVisible={false}
          continueButtonText={isAuthenticating ? 'Processing...' : isEditMode ? 'Save changes' : 'Continue'}
          handleClickContinue={handleContinue}
          continueButtonDisabled={!selectedMatter || !bundleName.trim() || !description.trim() || isAuthenticating}
        /></MUIStack>
    </>
  )
}